// lib/koi-pond-v3/shaders/causticFloor.ts
//
// Pond floor lit by animated caustic light patterns. Caustics are
// computed inline via a layered domain-warped voronoi noise — bright
// peaks drift slowly across a dark teal-black base. No precomputed
// texture (keeps bundle small + caustics are infinitely tile-free).
//
// Two octaves of voronoi-style cell noise, the second domain-warping
// the first. Cycle period ~14s — slow enough to be ambient, not
// distracting. Brightness scales by moon-direction alignment so the
// caustics are brightest under the moon's position (upper-right of
// world by default) and dimmer at frame edges.
//
// Phase 7 T7.1. moonDir is initially hardcoded (0.6, 1.0, -0.3) and
// gets replaced with the actual moon group position vector when T7.4
// mounts the moon. Material exposes uMoonWorldDir so the override is
// a single .copy() call from the scene mount, not a shader recompile.

import { ShaderMaterial, Vector3, Color } from 'three';
import { paletteVec3 } from '../palette';
import { DEPTH_FOG_UNIFORM_DECL, DEPTH_FOG_APPLY_SNIPPET } from './depthFog';

export const CAUSTIC_FLOOR_VERT = `
  varying vec2 vUv;
  varying vec3 vWorldPos;
  void main() {
    vUv = uv;
    vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const CAUSTIC_FLOOR_FRAG = `
  precision highp float;
  varying vec2 vUv;
  varying vec3 vWorldPos;
  uniform float uTime;
  uniform vec3 uMoonWorldDir;
  uniform float uDetail; // 1 = standard; >1 adds a fine shimmer octave (premium)
  uniform float uFlare;  // dragon-gate caustic flare (rest 0 = no-op)
  ${DEPTH_FOG_UNIFORM_DECL}

  vec2 hash2(vec2 p) {
    p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
    return fract(sin(p) * 43758.5453);
  }

  // Voronoi minimum-distance with animated cell points. Period ~14s
  // on the inner sin so the full caustic cycle loops every 14 seconds.
  // 0.4488 = 2π/14 — one full point-orbit per 14 seconds.
  float voronoi(vec2 uv, float t) {
    vec2 i = floor(uv);
    vec2 f = fract(uv);
    float minDist = 1.0;
    for (int y = -1; y <= 1; y++) {
      for (int x = -1; x <= 1; x++) {
        vec2 n = vec2(float(x), float(y));
        vec2 p = hash2(i + n);
        p = 0.5 + 0.5 * sin(t * 0.4488 + 6.2831 * p);
        minDist = min(minDist, length(n + p - f));
      }
    }
    return minDist;
  }

  void main() {
    // T7.6d — soft amorphous pools (direction B per owner sign-off).
    // Two octaves of voronoi layered with WIDE smoothstep falloff.
    // The big-slow octave dominates and produces broad bright pools
    // that bloom and dissipate; the small-fast octave adds subtle
    // shimmer texture WITHIN the pools. Both use wider smoothstep
    // ranges than instinct dictates — moonlit caustics are dim and
    // diffuse, not sharp daylight-pool-tile patterns. If a single
    // caustic pool ever reads as a distinct "shape," soften further.

    // Octave 1 — large slow cells (the dominant bright zones).
    // 2.0x UV scale = roughly 2 cells across the visible floor.
    // Wide smoothstep (0.1..0.85) gives broad bright bleed past cell
    // centers so adjacent cells overlap softly. Period 0.7x = slower
    // animation than T7.1's tight peaks.
    vec2 uv1 = vUv * 2.0;
    vec2 warp1 = vec2(
      voronoi(uv1 * 0.4 + uTime * 0.05, uTime * 0.7),
      voronoi(uv1 * 0.4 + 12.3 + uTime * 0.05, uTime * 0.7)
    );
    uv1 += warp1 * 0.5;
    float c1 = 1.0 - smoothstep(0.1, 0.85, voronoi(uv1, uTime * 0.7));

    // Octave 2 — small fast variation (shimmer within the broad pools).
    // 6.0x UV scale = denser cells, but their CONTRIBUTION is small
    // (30%) so they read as texture, not as distinct cells.
    vec2 uv2 = vUv * 6.0;
    vec2 warp2 = vec2(
      voronoi(uv2 * 0.4 + uTime * 0.12, uTime),
      voronoi(uv2 * 0.4 + 7.7 + uTime * 0.12, uTime)
    );
    uv2 += warp2 * 0.3;
    float c2 = 1.0 - smoothstep(0.0, 0.55, voronoi(uv2, uTime));

    // Octave 3 — premium-only fine shimmer (uDetail>1). Adds high-frequency
    // sparkle WITHIN the pools; zero on standard/lite so their approved look
    // is byte-for-byte unchanged.
    float c3 = 0.0;
    if (uDetail > 1.0) {
      vec2 uv3 = vUv * 11.0;
      uv3 += vec2(
        voronoi(uv3 * 0.5 + uTime * 0.18, uTime * 1.3),
        voronoi(uv3 * 0.5 + 3.1 + uTime * 0.18, uTime * 1.3)
      ) * 0.2;
      c3 = (1.0 - smoothstep(0.0, 0.5, voronoi(uv3, uTime * 1.3))) * (uDetail - 1.0) * 0.4;
    }

    // Composite — broad pools dominate, shimmer contributes a third.
    float c = c1 * 0.70 + c2 * 0.30 + c3;

    vec3 base = ${paletteVec3('deepWater')};
    vec3 peak = ${paletteVec3('causticPeak')};
    vec3 caustic = mix(base, peak, c);

    // Moon influence — caustics brighter where the floor position
    // points in the moon's direction. Floor sits at y=-3; moonDir
    // is a normalized world vector pointing from origin toward moon.
    vec3 floorPointDir = normalize(vWorldPos - vec3(0.0, -3.0, 0.0));
    float moonAlign = max(0.0, dot(floorPointDir, normalize(uMoonWorldDir)));
    float moonFalloff = mix(0.45, 1.0, moonAlign);  // lifted floor so the shadowed pond isn't near-black

    // Composite weight bumped from 0.6 to 0.75 — owner flagged scene
    // reads too uniformly dark; the slight luminance bump under the
    // moon's direction is supposed to be visible, not subtle.
    vec3 color = mix(base, caustic, 0.85 * moonFalloff);
    color *= (1.0 + 1.6 * uFlare); // dragon-gate detonation flare
    ${DEPTH_FOG_APPLY_SNIPPET}
    gl_FragColor = vec4(color, 1.0);
  }
`;

export function makeCausticFloorMaterial(
  moonDir: Vector3,
  sharedFogUniforms: { uFogColor: { value: Color }; uFogDensity: { value: number } },
  detail = 1,
): {
  material: ShaderMaterial;
  uniforms: {
    uTime: { value: number };
    uMoonWorldDir: { value: Vector3 };
    uDetail: { value: number };
    uFlare: { value: number };
    uFogColor: { value: Color };
    uFogDensity: { value: number };
  };
} {
  // Share fog uniforms BY REFERENCE so changing density in one place
  // updates every material on the next frame. Do NOT clone.
  const uniforms = {
    uTime: { value: 0 },
    uMoonWorldDir: { value: moonDir.clone() },
    uDetail: { value: detail },
    uFlare: { value: 0 },
    uFogColor: sharedFogUniforms.uFogColor,
    uFogDensity: sharedFogUniforms.uFogDensity,
  };
  const material = new ShaderMaterial({
    vertexShader: CAUSTIC_FLOOR_VERT,
    fragmentShader: CAUSTIC_FLOOR_FRAG,
    uniforms,
  });
  return { material, uniforms };
}
